import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import ToastStack, { useToasts } from '../../components/common/Toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { getAdminAccountList } from '../../api/account';
import { sendAdminPush, getPushHistory } from '../../api/adminPush';
import { adminPushBus } from '../../utils/adminPushBus';
import { formatDateTime } from '../../utils/format';

const targetLabel = (row) => {
  if (row.targetType === 'ACCOUNT') return `특정: ${row.targetAccount}`;
  return row.targetRole === 'STORE_ADMIN' ? '전체 매장관리자' : `역할: ${row.targetRole}`;
};

/**
 * 슈퍼관리자 전용: 로그인된 매장관리자에게 실시간 토스트 메시지 보내기.
 * 대상 = 전체 매장관리자(ROLE=STORE_ADMIN) 또는 특정 매장관리자(ACCOUNT).
 */
export default function AdminPushPage() {
  const { auth } = useAuth();
  const { toasts, pushToast, removeToast } = useToasts();
  const [targetType, setTargetType] = useState('ROLE'); // ROLE | ACCOUNT
  const [account, setAccount] = useState('');
  const [message, setMessage] = useState('');
  const [storeAdmins, setStoreAdmins] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false); // 발송 전 확인창
  const [history, setHistory] = useState([]); // DB 기반 발송 이력(수신확인 건수 포함)

  const fetchHistory = useCallback(async () => {
    try {
      const res = await getPushHistory();
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // 수신확인이 실시간으로 들어오면(RealtimeNotifier→adminPushBus) 이력의 수신확인 건수를 갱신한다.
  useEffect(() => {
    const unsub = adminPushBus.subscribe(() => { fetchHistory(); });
    return unsub;
  }, [fetchHistory]);

  // 특정 매장관리자 선택용 - 매장관리자(STORE_ADMIN) 계정 목록을 불러온다.
  const fetchStoreAdmins = useCallback(async () => {
    try {
      const res = await getAdminAccountList({ pageNo: 1, pageSize: 200 });
      const list = (res.data?.list || []).filter((a) => a.roleCode === 'STORE_ADMIN' && a.useYn !== 'N');
      setStoreAdmins(list);
    } catch {
      setStoreAdmins([]);
    }
  }, []);

  useEffect(() => { fetchStoreAdmins(); }, [fetchStoreAdmins]);

  const canSend = message.trim() && (targetType === 'ROLE' || account);

  // 확인창/토스트에 표시할 대상 설명
  const targetDesc = targetType === 'ROLE'
    ? '전체 매장관리자'
    : (storeAdmins.find((a) => a.adminAccount === account)
        ? `${storeAdmins.find((a) => a.adminAccount === account).adminName} (${account})`
        : (account || '특정 매장관리자'));

  const handleSend = async () => {
    if (!canSend || sending) return;
    setConfirmOpen(false);
    setSending(true);
    try {
      await sendAdminPush({
        targetType,
        roleCode: targetType === 'ROLE' ? 'STORE_ADMIN' : undefined,
        account: targetType === 'ACCOUNT' ? account : undefined,
        message: message.trim(),
        senderName: auth?.adminName,
      });
      const who = targetType === 'ROLE'
        ? '전체 매장관리자'
        : (storeAdmins.find((a) => a.adminAccount === account)?.adminName || account);
      pushToast(`${who}에게 메시지를 보냈습니다.`, 'info');
      setMessage('');
      fetchHistory();
    } catch (err) {
      pushToast(err.response?.data?.message || '메시지 발송에 실패했습니다.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title="실시간 알림" menuUrl="/admin/push-message">
      <div className="page-header">
        <div>
          <h1>실시간 알림 보내기</h1>
          <div className="subtitle">현재 관리자 사이트에 로그인해 있는 매장관리자에게 실시간 토스트 메시지를 보냅니다. 접속 중인 대상에게만 즉시 표시됩니다.</div>
        </div>
      </div>

      <div className="card card-padded" style={{ maxWidth: 640 }}>
        <div className="field">
          <label>대상</label>
          <div className="btn-group">
            <button type="button" className={`btn ${targetType === 'ROLE' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTargetType('ROLE')}>전체 매장관리자</button>
            <button type="button" className={`btn ${targetType === 'ACCOUNT' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTargetType('ACCOUNT')}>특정 매장관리자</button>
          </div>
        </div>

        {targetType === 'ACCOUNT' && (
          <div className="field">
            <label>매장관리자 선택</label>
            <select className="select" value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="">— 선택 —</option>
              {storeAdmins.map((a) => (
                <option key={a.adminId} value={a.adminAccount}>{a.adminName} ({a.adminAccount})</option>
              ))}
            </select>
            {storeAdmins.length === 0 && <div className="subtitle" style={{ marginTop: 6 }}>등록된 매장관리자 계정이 없습니다.</div>}
          </div>
        )}

        <div className="field">
          <label>메시지</label>
          <textarea
            className="input"
            style={{ minHeight: 100 }}
            value={message}
            maxLength={300}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="보낼 메시지를 입력하세요 (최대 300자)"
          />
        </div>

        <div className="flex-between" style={{ marginTop: 8 }}>
          <span className="subtitle">수신자가 로그인 중이 아니면 전달되지 않습니다(저장되지 않음).</span>
          <button type="button" className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={!canSend || sending}>
            {sending ? '보내는 중...' : '메시지 보내기'}
          </button>
        </div>
      </div>

      <div className="card card-padded" style={{ marginTop: 16 }}>
        <div className="flex-between" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 15 }}>발송 이력</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={fetchHistory}>새로고침</button>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>전송일시</th><th>대상</th><th>메시지</th><th>수신확인</th></tr></thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={4} className="empty-row">발송 이력이 없습니다.</td></tr>}
              {history.map((row) => (
                <tr key={row.messageId}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.sentAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{targetLabel(row)}</td>
                  <td style={{ whiteSpace: 'normal', maxWidth: 360 }}>{row.message}</td>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{row.ackCount}명 확인</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {confirmOpen && (
        <ConfirmDialog
          title="메시지 발송 확인"
          message={`'${targetDesc}' 에게 다음 메시지를 보냅니다.\n\n"${message.trim()}"\n\n발송하시겠습니까?`}
          confirmLabel="보내기"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleSend}
        />
      )}

      <ToastStack toasts={toasts} onDismiss={removeToast} />
    </AdminLayout>
  );
}
